import { supabase } from './supabase';

export type GoLink = {
  slug: string;
  destination_url: string;
  label: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  inCatalog?: boolean;
};

export type GoClick = {
  id: string;
  slug: string;
  destination_url: string;
  country: string | null;
  host: string | null;
  created_at: string;
};

export type GoLinkStats = GoLink & {
  clicks: number;
  lastClickedAt: string | null;
};

export const PRINTED_GO_SLUGS = ['card', 'sticker', 'phone'] as const;

export function goPublicUrl(slug: string): string {
  return `https://go.stephansmith.me/${slug}`;
}

export function isValidGoSlug(slug: string): boolean {
  return /^[a-z0-9][a-z0-9-]*$/.test(slug);
}

export function isPrintedGoSlug(slug: string): boolean {
  return (PRINTED_GO_SLUGS as readonly string[]).includes(slug);
}

export function aggregateGoClicks(links: GoLink[], clicks: GoClick[]): GoLinkStats[] {
  const bySlug = new Map<string, { clicks: number; lastClickedAt: string | null }>();
  for (const click of clicks) {
    const current = bySlug.get(click.slug) ?? { clicks: 0, lastClickedAt: null };
    current.clicks += 1;
    if (!current.lastClickedAt || click.created_at > current.lastClickedAt) {
      current.lastClickedAt = click.created_at;
    }
    bySlug.set(click.slug, current);
  }

  const known = new Set(links.map((link) => link.slug));
  const rows: GoLinkStats[] = links.map((link) => {
    const stats = bySlug.get(link.slug);
    return {
      ...link,
      inCatalog: true,
      clicks: stats?.clicks ?? 0,
      lastClickedAt: stats?.lastClickedAt ?? null,
    };
  });

  for (const [slug, stats] of bySlug) {
    if (known.has(slug)) continue;
    const sample = clicks.find((click) => click.slug === slug);
    rows.push({
      slug,
      destination_url: sample?.destination_url ?? '',
      label: null,
      notes: 'Seen in clicks — not in catalog',
      created_at: sample?.created_at ?? '',
      updated_at: sample?.created_at ?? '',
      inCatalog: false,
      clicks: stats.clicks,
      lastClickedAt: stats.lastClickedAt,
    });
  }

  return rows.sort((a, b) => b.clicks - a.clicks || a.slug.localeCompare(b.slug));
}

export function countryCounts(clicks: GoClick[]): { country: string; clicks: number }[] {
  const counts = new Map<string, number>();
  for (const click of clicks) {
    const country = click.country?.trim() || 'Unknown';
    counts.set(country, (counts.get(country) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([country, clicks]) => ({ country, clicks }))
    .sort((a, b) => b.clicks - a.clicks);
}

export async function loadGoLinksDashboard(): Promise<{
  links: GoLink[];
  clicks: GoClick[];
  stats: GoLinkStats[];
}> {
  const [linksResult, clicksResult] = await Promise.all([
    supabase.from('go_links').select('*').order('slug'),
    supabase
      .from('go_clicks')
      .select('id, slug, destination_url, country, host, created_at')
      .order('created_at', { ascending: false })
      .limit(2000),
  ]);

  if (linksResult.error) throw linksResult.error;
  if (clicksResult.error) throw clicksResult.error;

  const links = (linksResult.data ?? []) as GoLink[];
  const clicks = (clicksResult.data ?? []) as GoClick[];
  return { links, clicks, stats: aggregateGoClicks(links, clicks) };
}

export async function createGoLink(input: {
  slug: string;
  destination_url: string;
  label: string | null;
  notes: string | null;
}): Promise<void> {
  const { error } = await supabase.from('go_links').insert(input);
  if (error) throw error;
}

export async function updateGoLink(
  slug: string,
  input: {
    destination_url: string;
    label: string | null;
    notes: string | null;
  },
): Promise<void> {
  const { error } = await supabase
    .from('go_links')
    .update({ ...input, updated_at: new Date().toISOString() })
    .eq('slug', slug);
  if (error) throw error;
}

export async function deleteGoLink(slug: string): Promise<{ kind: 'catalog' | 'clicks' }> {
  if (isPrintedGoSlug(slug)) {
    throw new Error(`/${slug} is a printed QR — retarget it instead of deleting`);
  }
  const { data, error } = await supabase.from('go_links').delete().eq('slug', slug).select('slug');
  if (error) throw error;
  const { error: clickError } = await supabase.from('go_clicks').delete().eq('slug', slug);
  if (clickError) throw clickError;
  return { kind: data?.length ? 'catalog' : 'clicks' };
}
